import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import FormikInput from '../ui/FormikInput';
import clientesApiService from '../../services/http/clientesApiService';
import logger from '../../services/logger';

const validationSchema = Yup.object().shape({
  headquarter: Yup.string().when('$isNew', {
    is: true,
    then: (schema) => schema.required('La empresa es obligatoria'),
  }),
  nombreFiscal: Yup.string().required('El nombre fiscal es obligatorio'),
  cif: Yup.string().required('El CIF es obligatorio'),
  email: Yup.string().email('Email inválido'),
});

const FormularioCliente = ({ isNew = false }) => {
  const [loading, setLoading] = useState(!isNew);
  const { headquarter, ccodcli } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);

  useEffect(() => {
    if (!isNew && ccodcli && headquarter) {
      const fetchCliente = async () => {
        try {
          setLoading(true);
          const data = await clientesApiService.obtenerDetallesCliente({ ccodcli, headquarter });
          setCliente(data[0] || data);
        } catch (error) {
          logger.error('Error cargando cliente:', error);
          toast.error('Error al cargar los datos del cliente');
        } finally {
          setLoading(false);
        }
      };

      fetchCliente();
    } else if (isNew) {
      setCliente({
        nombreFiscal: '',
        nombreComercial: '',
        domicilio: '',
        poblacion: '',
        codigoPostal: '',
        provincia: '',
        pais: '',
        cif: '',
        telefono: '',
        movil: '',
        email: '',
        personaContacto: '',
        observaciones: '',
        headquarter: '',
      });
      setLoading(false);
    }
  }, [ccodcli, headquarter, isNew]);

  const handleGuardar = async (values, formikHelpers) => {
    try {
      setLoading(true);
      if (isNew) {
        const response = await clientesApiService.crearCliente(values);
        toast.success('Cliente creado correctamente');
        navigate(`/clientes/${values.headquarter}/${response.ccodcli}/detalles`);
      } else {
        await clientesApiService.actualizarCliente({ ...values, ccodcli, headquarter });
        toast.success('Cliente actualizado correctamente');
        const updatedData = await clientesApiService.obtenerDetallesCliente({
          ccodcli,
          headquarter,
        });
        setCliente(updatedData[0] || updatedData);
        formikHelpers.resetForm({ values: updatedData[0] || updatedData });
      }
    } catch (error) {
      logger.error('Error guardando cliente:', error);
      toast.error('Error al guardar el cliente');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" /> Cargando datos del cliente...
      </div>
    );
  }

  return (
    <>
      {cliente && (
        <Formik
          initialValues={cliente}
          onSubmit={handleGuardar}
          enableReinitialize
          validationSchema={validationSchema}
          context={{ isNew }}
        >
          {({ isSubmitting, values, setFieldValue, errors, touched }) => (
            <Form>
              {isNew && (
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="headquarter" className="form-label">
                      Empresa <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-control ${
                        errors.headquarter && touched.headquarter ? 'is-invalid' : ''
                      }`}
                      id="headquarter"
                      name="headquarter"
                      value={values.headquarter}
                      onChange={(e) => setFieldValue('headquarter', e.target.value)}
                    >
                      <option value="">Selecciona empresa</option>
                      <option value="CEDESCA">CEDESCA</option>
                      <option value="CNTEC">CNTEC</option>
                    </select>
                    {errors.headquarter && touched.headquarter && (
                      <div className="invalid-feedback d-block">{errors.headquarter}</div>
                    )}
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <FormikInput label="Nombre Fiscal" name="nombreFiscal" />
                </div>
                <div className="col-md-6">
                  <FormikInput label="Nombre Comercial" name="nombreComercial" />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-6">
                  <FormikInput label="CIF/NIF" name="cif" />
                </div>
                <div className="col-md-6">
                  <FormikInput label="Domicilio" name="domicilio" />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-4">
                  <FormikInput label="Población" name="poblacion" />
                </div>
                <div className="col-md-4">
                  <FormikInput label="Código Postal" name="codigoPostal" />
                </div>
                <div className="col-md-4">
                  <FormikInput label="Provincia" name="provincia" />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-4">
                  <FormikInput label="País" name="pais" />
                </div>
                <div className="col-md-4">
                  <FormikInput label="Teléfono" name="telefono" />
                </div>
                <div className="col-md-4">
                  <FormikInput label="Móvil" name="movil" />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-6">
                  <FormikInput label="Email" name="email" type="email" />
                </div>
                <div className="col-md-6">
                  <FormikInput label="Persona de Contacto" name="personaContacto" />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-12">
                  <FormikInput
                    label="Observaciones"
                    name="observaciones"
                    type="textarea"
                    placeholder="Escriba observaciones..."
                  />
                </div>
              </div>

              <div className="text-end mt-3">
                <Button
                  color="secondary"
                  className="me-2"
                  onClick={() => navigate('/clientes')}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button color="success" type="submit" disabled={isSubmitting}>
                  {isNew ? 'Crear' : 'Guardar'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

FormularioCliente.propTypes = {
  isNew: PropTypes.bool,
};

export default FormularioCliente;
